import type { Content, ContentType } from '@app-types/domain';
import { formatDuration, hmsToMin, minToHMS } from '@core/duration';
import { contentActions } from '@core/store';
import { importCSVText, importExcelFile } from '@services/excel-import';
import { Modal } from '@ui/components/Modal';
import { toast } from '@ui/components/Toast';
import { useStore } from '@ui/lib/useStore';
import { useState } from 'preact/hooks';

const TYPE_LABELS: Record<ContentType, string> = {
  video: 'Vídeo',
  reading: 'Leitura',
  exercise: 'Exercício',
  review: 'Revisão',
  other: 'Outro',
};

const PAGE_SIZE = 50;

export function ContentsScreen() {
  const contents = useStore((s) => s.contents);
  const goals = useStore((s) => s.goals);
  const [editing, setEditing] = useState<Content | null>(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [filter, setFilter] = useState('');
  const [goalFilter, setGoalFilter] = useState<string>('all'); // 'all' | 'none' | goalId
  const [sourceFilter, setSourceFilter] = useState<string>('all'); // 'all' | 'none' | source
  const [page, setPage] = useState(1);

  // metas e fontes que realmente aparecem nos conteúdos (para montar os botões)
  const goalsInUse = goals.filter((g) => contents.some((c) => c.goalId === g.id));
  const hasUnlinked = contents.some((c) => !c.goalId);
  const sourcesInUse = Array.from(
    new Set(contents.map((c) => c.source).filter((s): s is string => !!s)),
  ).sort((a, b) => a.localeCompare(b));
  const hasNoSource = contents.some((c) => !c.source);

  const filtered = contents.filter((c) => {
    if (filter && !c.title.toLowerCase().includes(filter.toLowerCase())) return false;
    if (goalFilter === 'none' && c.goalId) return false;
    if (goalFilter !== 'all' && goalFilter !== 'none' && c.goalId !== goalFilter) return false;
    if (sourceFilter === 'none' && c.source) return false;
    if (sourceFilter !== 'all' && sourceFilter !== 'none' && c.source !== sourceFilter)
      return false;
    return true;
  });

  // paginação: 50 por página, com clamp caso os filtros encolham a lista
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  // qualquer mudança de filtro/busca volta pra página 1
  function resetPage() {
    setPage(1);
  }

  const chipStyle = { fontSize: '10px', cursor: 'pointer' };

  return (
    <div>
      <div class="main-head">
        <div>
          <h1 class="main-title">Conteúdos</h1>
          <div class="main-sub">
            {contents.length} total · {contents.filter((c) => c.status === 'done').length} feitos
          </div>
        </div>
        <div class="main-actions">
          <input
            class="input"
            style={{ maxWidth: '220px' }}
            placeholder="filtrar..."
            value={filter}
            onInput={(e) => {
              setFilter((e.target as HTMLInputElement).value);
              resetPage();
            }}
          />
          {contents.length > 0 && (
            <button
              class="btn danger"
              onClick={() => {
                if (
                  !confirm(
                    `Apagar TODOS os ${contents.length} conteúdos?\n\nAs sessões vinculadas (incluindo histórico de Pomodoro) também serão apagadas. As metas ficam.`,
                  )
                )
                  return;
                if (!confirm('Tem certeza? Essa ação não tem volta.')) return;
                contentActions.clearAll();
                toast('Todos os conteúdos foram apagados', 'warn');
              }}
            >
              × LIMPAR TUDO
            </button>
          )}
          <button class="btn" onClick={() => setImporting(true)}>
            ↑ IMPORTAR
          </button>
          <button class="btn primary" onClick={() => setCreating(true)}>
            + NOVO
          </button>
        </div>
      </div>

      {contents.length === 0 ? (
        <div class="panel">
          <div class="empty-state">
            <div class="ico">▤</div>
            <h3>Nenhum conteúdo ainda</h3>
            <div>
              Adicione manualmente ou importe de Excel/CSV.
              <br />
              Formato aceito: colunas <b>título · duração · tipo · dificuldade</b>.
            </div>
            <div
              style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}
            >
              <button class="btn primary" onClick={() => setCreating(true)}>
                + ADICIONAR MANUAL
              </button>
              <button class="btn" onClick={() => setImporting(true)}>
                ↑ IMPORTAR PLANILHA
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* FILTROS POR META */}
          {(goalsInUse.length > 0 || hasUnlinked) && (
            <div style={{ marginBottom: '10px' }}>
              <div
                style={{
                  fontFamily: 'var(--f-mono)',
                  fontSize: '9px',
                  color: 'var(--text-dim)',
                  letterSpacing: '1px',
                  marginBottom: '6px',
                }}
              >
                META
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <button
                  class={'tag ' + (goalFilter === 'all' ? 'cyan' : 'dim')}
                  style={chipStyle}
                  onClick={() => {
                    setGoalFilter('all');
                    resetPage();
                  }}
                >
                  Todas
                </button>
                {goalsInUse.map((g) => (
                  <button
                    key={g.id}
                    class={'tag ' + (goalFilter === g.id ? 'cyan' : 'dim')}
                    style={chipStyle}
                    onClick={() => {
                      setGoalFilter(g.id);
                      resetPage();
                    }}
                  >
                    {g.title} · {contents.filter((c) => c.goalId === g.id).length}
                  </button>
                ))}
                {hasUnlinked && (
                  <button
                    class={'tag ' + (goalFilter === 'none' ? 'cyan' : 'dim')}
                    style={chipStyle}
                    onClick={() => {
                      setGoalFilter('none');
                      resetPage();
                    }}
                  >
                    Sem meta · {contents.filter((c) => !c.goalId).length}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* FILTROS POR FONTE */}
          {(sourcesInUse.length > 0 || hasNoSource) && (
            <div style={{ marginBottom: '16px' }}>
              <div
                style={{
                  fontFamily: 'var(--f-mono)',
                  fontSize: '9px',
                  color: 'var(--text-dim)',
                  letterSpacing: '1px',
                  marginBottom: '6px',
                }}
              >
                FONTE
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <button
                  class={'tag ' + (sourceFilter === 'all' ? 'purple' : 'dim')}
                  style={chipStyle}
                  onClick={() => {
                    setSourceFilter('all');
                    resetPage();
                  }}
                >
                  Todas
                </button>
                {sourcesInUse.map((src) => (
                  <button
                    key={src}
                    class={'tag ' + (sourceFilter === src ? 'purple' : 'dim')}
                    style={chipStyle}
                    onClick={() => {
                      setSourceFilter(src);
                      resetPage();
                    }}
                  >
                    {src} · {contents.filter((c) => c.source === src).length}
                  </button>
                ))}
                {hasNoSource && (
                  <button
                    class={'tag ' + (sourceFilter === 'none' ? 'purple' : 'dim')}
                    style={chipStyle}
                    onClick={() => {
                      setSourceFilter('none');
                      resetPage();
                    }}
                  >
                    Sem fonte · {contents.filter((c) => !c.source).length}
                  </button>
                )}
              </div>
            </div>
          )}

          <div class="panel scroll-x">
            <table class="table" style={{ minWidth: '640px' }}>
              <thead>
                <tr>
                  <th>Título</th>
                  <th>Tipo</th>
                  <th>Duração</th>
                  <th>Dif.</th>
                  <th>Meta</th>
                  <th>Fonte</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pageItems.map((c) => {
                  const goal = goals.find((g) => g.id === c.goalId);
                  return (
                    <tr key={c.id}>
                      <td style={{ color: 'var(--text)', fontWeight: 500 }}>{c.title}</td>
                      <td>
                        <span class="tag purple">{TYPE_LABELS[c.type]}</span>
                      </td>
                      <td>{formatDuration(c.duration_min)}</td>
                      <td>
                        <span class="tag amber">{c.difficulty}</span>
                      </td>
                      <td>
                        {goal ? <span class="tag">{goal.title}</span> : <span class="dim">—</span>}
                      </td>
                      <td>
                        {c.source ? (
                          <span class="tag dim">{c.source}</span>
                        ) : (
                          <span class="dim">—</span>
                        )}
                      </td>
                      <td>
                        <span
                          class={
                            'tag ' +
                            (c.status === 'done'
                              ? 'green'
                              : c.status === 'doing'
                                ? 'pink'
                                : c.status === 'skipped'
                                  ? 'dim'
                                  : '')
                          }
                        >
                          {c.status}
                        </span>
                      </td>
                      <td>
                        <button class="btn sm ghost" onClick={() => setEditing(c)}>
                          EDIT
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div class="empty-state" style={{ padding: '30px 0' }}>
                Nada bate com os filtros.
              </div>
            )}
          </div>

          {/* PAGINAÇÃO */}
          {filtered.length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                marginTop: '14px',
                flexWrap: 'wrap',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--f-mono)',
                  fontSize: '11px',
                  color: 'var(--text-dim)',
                }}
              >
                {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filtered.length)} de{' '}
                {filtered.length}
                {filtered.length !== contents.length && ` (${contents.length} no total)`}
              </div>
              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <button
                    class="btn sm ghost"
                    disabled={safePage <= 1}
                    onClick={() => setPage(safePage - 1)}
                  >
                    ← ANTERIOR
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      class={'btn sm ' + (p === safePage ? 'primary' : 'ghost')}
                      style={{ minWidth: '34px' }}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    class="btn sm ghost"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage(safePage + 1)}
                  >
                    PRÓXIMA →
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <ContentModal
        open={creating || editing !== null}
        content={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
      />
      <ImportModal open={importing} onClose={() => setImporting(false)} />
    </div>
  );
}

// ===== CONTENT MODAL =====
function ContentModal(props: { open: boolean; content: Content | null; onClose: () => void }) {
  const goals = useStore((s) => s.goals);
  const settings = useStore((s) => s.settings);
  const defaultGoalId =
    props.content?.goalId || settings.active_goal_id || goals[0]?.id || undefined;

  const initialHMS = minToHMS(props.content?.duration_min ?? 30);
  const [title, setTitle] = useState(props.content?.title || '');
  const [type, setType] = useState<ContentType>(props.content?.type || 'video');
  const [durH, setDurH] = useState(initialHMS.h);
  const [durM, setDurM] = useState(initialHMS.m);
  const [durS, setDurS] = useState(initialHMS.s);
  const [difficulty, setDifficulty] = useState<1 | 2 | 3 | 4 | 5>(props.content?.difficulty || 3);
  const [goalId, setGoalId] = useState<string | undefined>(defaultGoalId);
  const [source, setSource] = useState(props.content?.source || '');

  // Reset on prop change
  const isEdit = props.content !== null;
  const key = props.content?.id || 'new';

  return (
    <Modal
      key={key}
      open={props.open}
      onClose={props.onClose}
      title={isEdit ? 'Editar conteúdo' : 'Novo conteúdo'}
      actions={
        <>
          {isEdit && (
            <button
              class="btn danger"
              onClick={() => {
                if (confirm(`Deletar "${props.content!.title}"?`)) {
                  contentActions.remove(props.content!.id);
                  toast('Conteúdo removido', 'warn');
                  props.onClose();
                }
              }}
            >
              DELETAR
            </button>
          )}
          <button class="btn" onClick={props.onClose}>
            CANCELAR
          </button>
          <button
            class="btn primary"
            onClick={() => {
              if (!title.trim()) {
                toast('Título obrigatório', 'err');
                return;
              }
              const duration_min = hmsToMin(durH, durM, durS);
              if (duration_min <= 0) {
                toast('Duração precisa ser maior que zero', 'err');
                return;
              }
              if (isEdit) {
                contentActions.update(props.content!.id, {
                  title: title.trim(),
                  type,
                  duration_min,
                  difficulty,
                  goalId,
                  source: source.trim() || undefined,
                });
                toast('Conteúdo atualizado', 'ok');
              } else {
                contentActions.add({
                  title: title.trim(),
                  type,
                  duration_min,
                  difficulty,
                  goalId,
                  source: source.trim() || undefined,
                  status: 'todo',
                });
                toast('Conteúdo criado', 'ok');
              }
              props.onClose();
            }}
          >
            {isEdit ? 'SALVAR' : 'CRIAR'}
          </button>
        </>
      }
    >
      <div class="field">
        <label>Título</label>
        <input
          class="input"
          value={title}
          onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
          placeholder="ex: Módulo 1 · Fundamentos"
        />
      </div>
      <div class="field">
        <label>Tipo</label>
        <select
          class="select"
          value={type}
          onChange={(e) => setType((e.target as HTMLSelectElement).value as ContentType)}
        >
          <option value="video">Vídeo</option>
          <option value="reading">Leitura</option>
          <option value="exercise">Exercício</option>
          <option value="review">Revisão</option>
          <option value="other">Outro</option>
        </select>
      </div>
      <div class="field">
        <label>Duração</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
          <div style={{ position: 'relative' }}>
            <input
              class="input"
              type="number"
              min="0"
              max="99"
              value={durH}
              onInput={(e) =>
                setDurH(Math.max(0, Number.parseInt((e.target as HTMLInputElement).value, 10) || 0))
              }
            />
            <span
              style={{
                position: 'absolute',
                right: '28px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-dim)',
                fontFamily: 'var(--f-mono)',
                fontSize: '11px',
                pointerEvents: 'none',
              }}
            >
              h
            </span>
          </div>
          <div style={{ position: 'relative' }}>
            <input
              class="input"
              type="number"
              min="0"
              max="59"
              value={durM}
              onInput={(e) =>
                setDurM(
                  Math.min(
                    59,
                    Math.max(0, Number.parseInt((e.target as HTMLInputElement).value, 10) || 0),
                  ),
                )
              }
            />
            <span
              style={{
                position: 'absolute',
                right: '28px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-dim)',
                fontFamily: 'var(--f-mono)',
                fontSize: '11px',
                pointerEvents: 'none',
              }}
            >
              min
            </span>
          </div>
          <div style={{ position: 'relative' }}>
            <input
              class="input"
              type="number"
              min="0"
              max="59"
              value={durS}
              onInput={(e) =>
                setDurS(
                  Math.min(
                    59,
                    Math.max(0, Number.parseInt((e.target as HTMLInputElement).value, 10) || 0),
                  ),
                )
              }
            />
            <span
              style={{
                position: 'absolute',
                right: '28px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-dim)',
                fontFamily: 'var(--f-mono)',
                fontSize: '11px',
                pointerEvents: 'none',
              }}
            >
              s
            </span>
          </div>
        </div>
        <div
          style={{
            marginTop: '6px',
            fontFamily: 'var(--f-mono)',
            fontSize: '11px',
            color: 'var(--cyan)',
          }}
        >
          = {formatDuration(hmsToMin(durH, durM, durS))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
        <div class="field">
          <label>Dificuldade (1-5)</label>
          <select
            class="select"
            value={difficulty}
            onChange={(e) =>
              setDifficulty(
                Number.parseInt((e.target as HTMLSelectElement).value, 10) as 1 | 2 | 3 | 4 | 5,
              )
            }
          >
            <option value="1">1 · muito fácil</option>
            <option value="2">2 · fácil</option>
            <option value="3">3 · médio</option>
            <option value="4">4 · difícil</option>
            <option value="5">5 · muito difícil</option>
          </select>
        </div>
        <div class="field">
          <label>Meta (opcional)</label>
          <select
            class="select"
            value={goalId || ''}
            onChange={(e) => setGoalId((e.target as HTMLSelectElement).value || undefined)}
          >
            <option value="">— nenhuma —</option>
            {goals.map((g) => (
              <option key={g.id} value={g.id}>
                {g.title}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div class="field">
        <label>Fonte (curso, livro, etc — opcional)</label>
        <input
          class="input"
          value={source}
          onInput={(e) => setSource((e.target as HTMLInputElement).value)}
          placeholder="ex: Curso ANBIMA CPA-20"
        />
      </div>
    </Modal>
  );
}

// ===== IMPORT MODAL =====
function ImportModal(props: { open: boolean; onClose: () => void }) {
  const goals = useStore((s) => s.goals);
  const settings = useStore((s) => s.settings);
  const [mode, setMode] = useState<'file' | 'csv'>('file');
  const [csvText, setCsvText] = useState('');
  const [goalId, setGoalId] = useState<string | undefined>(settings.active_goal_id || goals[0]?.id);
  const [processing, setProcessing] = useState(false);

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      title="Importar conteúdos"
      maxWidth={600}
      actions=<button class="btn" onClick={props.onClose}>
        FECHAR
      </button>
    >
      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
        <button
          class={'btn sm ' + (mode === 'file' ? 'primary' : 'ghost')}
          onClick={() => setMode('file')}
        >
          ARQUIVO XLSX/XLS
        </button>
        <button
          class={'btn sm ' + (mode === 'csv' ? 'primary' : 'ghost')}
          onClick={() => setMode('csv')}
        >
          COLAR CSV
        </button>
      </div>

      <div class="field">
        <label>Vincular à meta (opcional)</label>
        <select
          class="select"
          value={goalId || ''}
          onChange={(e) => setGoalId((e.target as HTMLSelectElement).value || undefined)}
        >
          <option value="">— nenhuma —</option>
          {goals.map((g) => (
            <option key={g.id} value={g.id}>
              {g.title}
            </option>
          ))}
        </select>
      </div>

      <div
        style={{
          background: 'rgba(0,240,255,0.05)',
          border: '1px solid var(--border-soft)',
          borderRadius: '8px',
          padding: '12px 14px',
          fontSize: '12px',
          color: 'var(--text-soft)',
          marginBottom: '14px',
          lineHeight: 1.55,
        }}
      >
        <b style={{ color: 'var(--cyan)' }}>Colunas aceitas:</b> titulo/nome/aula · duracao/minutos
        · tipo · dificuldade · fonte. A primeira linha é o cabeçalho. Duração aceita{' '}
        <b style={{ color: 'var(--cyan)' }}>01:30:45</b>,{' '}
        <b style={{ color: 'var(--cyan)' }}>12:34</b> ou minutos.
        <div style={{ marginTop: '10px' }}>
          <a
            class="btn sm gold"
            href="/modelo-importacao-robox.xlsx"
            download
            style={{ textDecoration: 'none' }}
          >
            ↓ BAIXAR PLANILHA MODELO
          </a>
        </div>
      </div>

      {mode === 'file' && (
        <div class="field">
          <label>Selecione arquivo Excel</label>
          <input
            class="input"
            type="file"
            accept=".xlsx,.xls,.csv"
            disabled={processing}
            onChange={async (e) => {
              const file = (e.target as HTMLInputElement).files?.[0];
              if (!file) return;
              setProcessing(true);
              try {
                const preview = file.name.endsWith('.csv')
                  ? importCSVText(await file.text(), goalId)
                  : await importExcelFile(file, goalId);
                if (preview.drafts.length === 0) {
                  toast('Nenhum conteúdo válido encontrado', 'err');
                  if (preview.errors.length > 0) console.warn('Import errors:', preview.errors);
                } else {
                  const added = contentActions.bulkAdd(preview.drafts);
                  toast(
                    `✓ ${added} conteúdo(s) importado(s)` +
                      (preview.errors.length > 0 ? ` · ${preview.errors.length} ignorado(s)` : ''),
                    'ok',
                    4000,
                  );
                  props.onClose();
                }
              } catch (err) {
                console.error(err);
                toast('Erro ao importar: ' + (err as Error).message, 'err');
              } finally {
                setProcessing(false);
                (e.target as HTMLInputElement).value = '';
              }
            }}
          />
        </div>
      )}

      {mode === 'csv' && (
        <div class="field">
          <label>Cole o conteúdo CSV (separado por vírgula, ponto e vírgula ou tab)</label>
          <textarea
            class="textarea"
            style={{ minHeight: '160px' }}
            placeholder={'titulo,duracao,tipo\nAula 1,30,video\nAula 2,45,exercicio'}
            value={csvText}
            onInput={(e) => setCsvText((e.target as HTMLTextAreaElement).value)}
          />
          <button
            class="btn primary"
            style={{ marginTop: '10px' }}
            disabled={processing || !csvText.trim()}
            onClick={() => {
              const preview = importCSVText(csvText, goalId);
              if (preview.drafts.length === 0) {
                toast('Nenhum conteúdo válido encontrado', 'err');
              } else {
                const added = contentActions.bulkAdd(preview.drafts);
                toast(`✓ ${added} conteúdo(s) importado(s)`, 'ok');
                setCsvText('');
                props.onClose();
              }
            }}
          >
            IMPORTAR CSV
          </button>
        </div>
      )}
    </Modal>
  );
}
